import React, { useEffect, useState, useRef } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
interface VideoPlayerProps {
  src: string;
  poster?: string;
  loop?: boolean;
  autoPlay?: boolean;
  muted?: boolean; // Controlled muted state
  volume?: number; // Controlled volume state (0-1)
  onToggleMute?: (isMuted: boolean) => void; // Callback for mute toggle
  showSoundButton?: boolean; // New prop to control button visibility
  className?: string; // Container class override
  videoClassName?: string; // Video element class override
}
export function VideoPlayer({
  src,
  poster,
  loop = true,
  autoPlay = true,
  muted: externalMuted,
  volume: externalVolume,
  onToggleMute,
  showSoundButton = true,
  className,
  videoClassName
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [internalMuted, setInternalMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  // Use external props if provided, otherwise internal state
  const isMuted = externalMuted !== undefined ? externalMuted : internalMuted;
  // Sync volume and muted state with video element
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    // Sync muted state
    if (video.muted !== isMuted) {
      video.muted = isMuted;
    }
    // Sync volume if provided
    if (externalVolume !== undefined) {
      video.volume = Math.max(0, Math.min(1, externalVolume));
    }
  }, [isMuted, externalVolume]);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (autoPlay) {
      video.muted = true; // Always start muted for autoplay policy
      // If externalMuted is false, we try to unmute immediately if user interaction allowed (rarely works without interaction)
      // But usually we respect the prop. If prop says unmuted, we try to unmute.
      if (externalMuted === false) {
        video.muted = false;
      }
      video.play().catch(err => {
        console.warn('Autoplay failed:', err);
        // Fallback: mute and try again if failed (though we started muted)
        video.muted = true;
        video.play().catch(e => console.error('Muted autoplay also failed', e));
      });
    }
  }, [autoPlay, src]);
  const handleToggleMute = () => {
    const newMutedState = !isMuted;
    if (videoRef.current) {
      videoRef.current.muted = newMutedState;
    }
    // Update internal state
    setInternalMuted(newMutedState);
    // Notify parent
    if (onToggleMute) {
      onToggleMute(newMutedState);
    }
  };
  // Default classes
  const containerClasses = className ? `relative overflow-hidden shadow-2xl shadow-orange-500/20 border border-slate-800 bg-black group ${className}` : 'relative w-full aspect-video rounded-xl overflow-hidden shadow-2xl shadow-orange-500/20 border border-slate-800 bg-black group';
  const videoClasses = videoClassName ? `object-cover ${videoClassName}` : 'w-full h-full object-cover';
  return <div className={containerClasses} data-id="element-76">
      <video ref={videoRef} src={src} poster={poster} className={videoClasses} loop={loop} playsInline muted={isMuted} onPlay={() => setIsPlaying(true)} data-id="element-77" />

      {showSoundButton && <button onClick={handleToggleMute} className="absolute bottom-3 left-3 z-20 flex items-center justify-center w-8 h-8 bg-black/60 backdrop-blur-sm text-white rounded-full border border-white/20 hover:bg-black/80 transition-colors" aria-label={isMuted ? 'Activar sonido' : 'Desactivar sonido'} data-id="element-78">
          {isMuted ? <VolumeX className="w-4 h-4" data-id="element-79" /> : <Volume2 className="w-4 h-4" data-id="element-80" />}
        </button>}
    </div>;
}